import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { TreatmentReminderTarget } from '@/frontend/features/patient/reminder-targets'
import { useOsApi } from '@/frontend/hooks/api/use-os-api'

export function useAddTreatmentReminder({
  patientId,
  consultationId,
}: {
  patientId: string
  consultationId?: string
}) {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (vars: {
      treatmentId: string
      target: TreatmentReminderTarget
      dueAt: string
    }) => {
      const response = await api.post('/treatments/:id/reminders', {
        params: { id: vars.treatmentId },
        body: { target: vars.target, dueAt: vars.dueAt },
      })
      if (!response.success) throw new Error(response.error.message)
      return response.data.reminder
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-treatments', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-timeline', patientId] })
      // In a consultation, the treatment rows come from the consultation-detail query.
      if (consultationId) {
        queryClient.invalidateQueries({ queryKey: ['consultation', consultationId] })
      }
    },
    // Surfaces the server message — most usefully the 409 when an active reminder already
    // covers this (treatment, target), which the partial unique index rejects.
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to add reminder')
    },
  })
}
